import { FunctionComponent } from 'react';

import ArchLogo from '@Assets/images/arch-logo.svg';
import BaseLayout from '@Components/BaseLayout';
import Header from '@Components/Header';
import SideBarMenu, { SideBarMenuItem } from '@Components/SideBarMenu';
import { CommonConstants } from '@Constants/index';
import RoutesConstants from '@Constants/routes.constants';
import { decodeToken } from '@Services/token.service';
import { logout } from '@Slices/auth.slice';
import { useAppDispatch, useAppSelector } from '@Utils/hooks/useStore.hook';

import BaseContainerProps from './Base.types';

const Base: FunctionComponent<BaseContainerProps> = (
  props: BaseContainerProps
): JSX.Element => {
  const dispatch = useAppDispatch();

  /** Extract Username from accessToken */
  const authState = useAppSelector((state) => state.auth);
  const token = authState.accessToken || '';
  const decoded = decodeToken(token);
  const { firstName, lastName } = decoded;

  // TODO: Add Translations to Label
  const sidebarMenuItems: SideBarMenuItem[] = [
    {
      label: 'Users',
      icon: 'users',
      to: RoutesConstants.ADMIN_ROUTES.USERS,
      toParams: RoutesConstants.SEARCH_PARAMS.USERS,
      accessRights: [CommonConstants.USER_ROLES.ADMIN],
    },
    {
      label: 'Report',
      icon: 'summarize',
      to: RoutesConstants.REPORT_ROUTES.BASE,
      accessRights: [CommonConstants.USER_ROLES.USER],
    },
    {
      label: 'Report',
      icon: 'summarize',
      to: RoutesConstants.ADMIN_ROUTES.REPORT,
      accessRights: [CommonConstants.USER_ROLES.ADMIN],
    },
    {
      label: 'Scripts',
      icon: 'speakerNotes',
      accessRights: [CommonConstants.USER_ROLES.USER],
      children: [
        {
          label: 'Active',
          to: RoutesConstants.SCRIPTS_ROUTES.ACTIVE,
          toParams: RoutesConstants.SEARCH_PARAMS.BASE,
          accessRights: [CommonConstants.USER_ROLES.USER],
        },
        {
          label: 'Archived',
          to: RoutesConstants.SCRIPTS_ROUTES.ARCHIVED,
          toParams: RoutesConstants.SEARCH_PARAMS.BASE,
          accessRights: [CommonConstants.USER_ROLES.USER],
        },
      ],
    },
    {
      label: 'Global Scripts',
      icon: 'speakerNotes',
      accessRights: [CommonConstants.USER_ROLES.ADMIN],
      to: RoutesConstants.ADMIN_ROUTES.GLOBAL_SCRIPTS,
      toParams: RoutesConstants.SEARCH_PARAMS.BASE,
    },
    {
      label: 'Global Emails',
      icon: 'mail',
      accessRights: [CommonConstants.USER_ROLES.ADMIN],
      to: RoutesConstants.ADMIN_ROUTES.GLOBAL_EMAILS,
      toParams: RoutesConstants.SEARCH_PARAMS.BASE,
    },
    {
      label: 'Email',
      icon: 'mail',
      accessRights: [CommonConstants.USER_ROLES.USER],
      children: [
        {
          label: 'Templates',
          to: RoutesConstants.EMAILS_ROUTES.TEMPLATES,
          accessRights: [CommonConstants.USER_ROLES.USER],
          children: [
            {
              label: 'Active',
              to: RoutesConstants.EMAILS_ROUTES.ACTIVE,
              toParams: RoutesConstants.SEARCH_PARAMS.BASE,
              accessRights: [CommonConstants.USER_ROLES.USER],
              isNestedSecondLevel: true,
            },
            {
              label: 'Archived',
              to: RoutesConstants.EMAILS_ROUTES.ARCHIVED,
              toParams: RoutesConstants.SEARCH_PARAMS.BASE,
              accessRights: [CommonConstants.USER_ROLES.USER],
              isNestedSecondLevel: true,
            },
          ],
        },
        {
          label: 'SMTP Settings',
          to: RoutesConstants.EMAILS_ROUTES.SMTP,
          accessRights: [CommonConstants.USER_ROLES.USER],
        },
      ],
    },
    {
      label: 'User Caller IDs',
      icon: 'phone',
      to: RoutesConstants.ADMIN_ROUTES.USER_CALLER_IDS,
      accessRights: [CommonConstants.USER_ROLES.USER],
    },
    {
      label: 'DNC',
      icon: 'dnc',
      accessRights: [CommonConstants.USER_ROLES.USER],
      to: RoutesConstants.DNC_ROUTES.BASE,
      toParams: RoutesConstants.SEARCH_PARAMS.DNC,
    },
    {
      label: 'Global DNC',
      icon: 'dnc',
      accessRights: [CommonConstants.USER_ROLES.ADMIN],
      to: RoutesConstants.ADMIN_ROUTES.GLOBAL_DNC,
      toParams: RoutesConstants.SEARCH_PARAMS.DNC,
    },
    {
      label: 'Groups',
      icon: 'libraryBooks',
      accessRights: [CommonConstants.USER_ROLES.ADMIN],
      to: RoutesConstants.ADMIN_ROUTES.GROUPS,
      toParams: RoutesConstants.SEARCH_PARAMS.BASE,
    },
    {
      label: 'Caller IDs Checker',
      icon: 'lookup',
      accessRights: [CommonConstants.USER_ROLES.ADMIN],
      to: RoutesConstants.ADMIN_ROUTES.CALLER_IDS_CHECKER,
    },
    {
      label: 'Settings',
      icon: 'settings',
      accessRights: [CommonConstants.USER_ROLES.ADMIN],
      to: RoutesConstants.ADMIN_ROUTES.ADMIN_SETTINGS,
    },
    {
      label: 'Account Settings',
      icon: 'accountSettings',
      accessRights: [
        CommonConstants.USER_ROLES.ADMIN,
        CommonConstants.USER_ROLES.USER,
      ],
      to: RoutesConstants.ACCOUNT_SETTINGS_ROUTES.BASE,
    },
  ];
  /** On Logout Button Click
   *
   * Removes all tokens and redirects to Login Route.
   */
  const onLogout = (): void => {
    dispatch<{
      payload: undefined;
      type: string;
    }>(logout());
  };

  return (
    <BaseLayout
      sideMenu={<SideBarMenu items={sidebarMenuItems} logo={ArchLogo} />}
      header={
        <Header username={`${firstName} ${lastName}`} onLogout={onLogout} />
      }
      {...props}
    />
  );
};

export default Base;
